'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
   getConsentFromCookie,
   getPiwikConsentFromCookie,
   setConsentCookie,
} from './consent-storage';
import {
   CONSENT_ACCEPTED,
   CONSENT_PENDING,
   CONSENT_REJECTED,
   type ConsentDecision,
} from './consent-types';
import { trackCustomGAEvent } from '@ifixit/analytics';

export function useConsentCookie(piwikSiteId: string) {
   const [isReady, setIsReady] = useState(false);
   const [consentDecision, setConsentDecision] = useState<ConsentDecision>('pending');

   useEffect(() => {
      let cancelled = false;

      const consent = getConsentFromCookie();
      if (consent) {
         setConsentDecision(
            consent.isConsented === CONSENT_ACCEPTED
               ? 'accepted'
               : consent.isConsented === CONSENT_REJECTED
                 ? 'rejected'
                 : 'pending'
         );
      } else {
         // TODO: Remove Piwik fallback after all Piwik cookies expire (~Mar 2027)
         const piwikConsent = getPiwikConsentFromCookie(piwikSiteId);
         if (piwikConsent === true) {
            setConsentDecision('accepted');
            setConsentCookie(CONSENT_ACCEPTED);
         } else if (piwikConsent === false) {
            setConsentDecision('rejected');
            setConsentCookie(CONSENT_REJECTED);
         } else {
            if (cancelled) {
               return;
            }
            trackCustomGAEvent({
               eventAction: 'Consent Banner Impression',
               eventCategory: 'Consent',
            });
            setConsentDecision('pending');
            setConsentCookie(CONSENT_PENDING);
         }
      }
      if (cancelled) {
         return;
      }
      setIsReady(true);

      return () => {
         cancelled = true;
      };
   }, [piwikSiteId]);

   const acceptAll = useCallback(async () => {
      setConsentCookie(CONSENT_ACCEPTED);
      setConsentDecision('accepted');
   }, []);

   const rejectAll = useCallback(async () => {
      setConsentCookie(CONSENT_REJECTED);
      setConsentDecision('rejected');
   }, []);

   return useMemo(
      () => ({ isReady, consentDecision, acceptAll, rejectAll }),
      [isReady, consentDecision, acceptAll, rejectAll]
   );
}
